import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  Configuration,
  FetchError,
  HandlerIncidentListResponse,
  instanceOfHandlerErrorResponse,
  InternalApi,
  ResponseError,
  V1IncidentsGetRequest,
} from '@internal/backstage-plugin-neo4j-incidents-common';

import { INCIDENT_API_BASE_URL } from './const';
import { IncidentApiError } from './errors';

const DEFAULT_ERROR_MESSAGE = 'Unknown error';
const DEFAULT_ERROR_STATUS = 400;

function errorMessageFrom(error: unknown): string {
  if (error instanceof FetchError) {
    return error.cause.message ?? error.message ?? DEFAULT_ERROR_MESSAGE;
  }

  return DEFAULT_ERROR_MESSAGE;
}

export class IncidentRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApiBackstage: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApiBackstage = options.fetchApi;
  }

  private async getIncidentApi(): Promise<InternalApi> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');

    return new InternalApi(
      new Configuration({
        basePath: `${baseUrl}${INCIDENT_API_BASE_URL}`,
        fetchApi: this.fetchApiBackstage.fetch,
      }),
    );
  }

  async getIncidents(
    requestParameters: V1IncidentsGetRequest = {},
  ): Promise<HandlerIncidentListResponse> {
    try {
      const api = await this.getIncidentApi();
      return await api.v1IncidentsGet(requestParameters);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const json = await error.response.json();

      if (instanceOfHandlerErrorResponse(json)) {
        const fallback = json.error ?? DEFAULT_ERROR_MESSAGE;

        throw new IncidentApiError(fallback, error.response.status);
      }
    }

    throw new IncidentApiError(errorMessageFrom(error), DEFAULT_ERROR_STATUS);
  }
}

export const incidentApiRef: ApiRef<IncidentRestApi> =
  createApiRef<IncidentRestApi>({
    id: 'plugin.neo4j-incidents-react.service',
  });
